/**
 * Máscaras e validação de CPF, CNPJ e telefone — o que todo campo desses usa.
 *
 * ## Máscara na tela, dígitos no banco
 *
 * O servidor guarda só dígitos (é a chave que casa com o SPED e com a Receita)
 * e aceita valor com máscara — ele mesmo limpa. Por isso as máscaras aqui são
 * livres para formatar à vontade: o que sai do formulário continua sendo o que
 * o servidor espera.
 *
 * ## Validação idêntica à do domínio
 *
 * `cpfValido` e `cnpjValido` são cópias de `@deca/domain` (módulo 11, os
 * mesmos pesos, repetidos recusados). O web não importa o pacote de domínio,
 * e um validador "parecido" é como o front passa a aceitar o que o servidor
 * recusa — a lista de 15/09 (item 5) pede as duas pontas conferindo.
 */

export function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/** `12345678909` → `123.456.789-09`, formatando conforme se digita. */
export function mascararCpf(valor: string): string {
  const d = somenteDigitos(valor).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
}

/** `12345678000199` → `12.345.678/0001-99`, formatando conforme se digita. */
export function mascararCnpj(valor: string): string {
  const d = somenteDigitos(valor).slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
}

/** CPF até 11 dígitos; a partir do 12º vira CNPJ — o campo "titular" aceita os dois. */
export function mascararCpfOuCnpj(valor: string): string {
  const d = somenteDigitos(valor);
  return d.length <= 11 ? mascararCpf(d) : mascararCnpj(d);
}

/**
 * `43999998888` → `(43) 99999-8888`; fixo `4333221100` → `(43) 3322-1100`.
 * Um `55` na frente de 12/13 dígitos é o DDI colado — sai da máscara.
 */
export function mascararTelefone(valor: string): string {
  let d = somenteDigitos(valor);
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  d = d.slice(0, 11);

  if (d.length <= 2) return d.length === 0 ? '' : `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function cpfValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length !== 11 || todosIguais(digitos)) return false;

  return (
    digitoVerificador(digitos, 9, 10) === Number(digitos[9]) &&
    digitoVerificador(digitos, 10, 11) === Number(digitos[10])
  );
}

export function cnpjValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length !== 14 || todosIguais(digitos)) return false;

  return (
    digitoPorPesos(digitos, PESOS_CNPJ_1) === Number(digitos[12]) &&
    digitoPorPesos(digitos, PESOS_CNPJ_2) === Number(digitos[13])
  );
}

export function documentoValido(valor: string): boolean {
  const digitos = somenteDigitos(valor);
  if (digitos.length === 11) return cpfValido(digitos);
  if (digitos.length === 14) return cnpjValido(digitos);
  return false;
}

/** Telefone brasileiro: DDD + 8 (fixo) ou 9 (celular) dígitos. */
export function telefoneValido(valor: string): boolean {
  const d = somenteDigitos(mascararTelefone(valor));
  return d.length === 10 || d.length === 11;
}

export type TipoDeDocumento = 'cpf' | 'cnpj' | 'cpf-ou-cnpj';

/**
 * A mensagem de erro de um documento, ou `undefined` quando está certo.
 *
 * Vazio não é erro — obrigatoriedade é do formulário, não da máscara. E um
 * documento **incompleto** só acusa quando `completo` é `true` (o campo perdeu
 * o foco ou o formulário tentou enviar): apontar erro enquanto a pessoa ainda
 * digita é o campo gritando a cada tecla.
 */
export function erroDoDocumento(
  valor: string,
  tipo: TipoDeDocumento,
  completo: boolean,
): string | undefined {
  const d = somenteDigitos(valor);
  if (d.length === 0) return undefined;

  const esperado = tipo === 'cpf' ? 11 : tipo === 'cnpj' ? 14 : d.length <= 11 ? 11 : 14;
  if (d.length < esperado) {
    return completo ? `${nomeDoDocumento(tipo, d)} incompleto — faltam dígitos.` : undefined;
  }
  return documentoValido(d)
    ? undefined
    : `${nomeDoDocumento(tipo, d)} inválido — confira os dígitos.`;
}

export function erroDoTelefone(valor: string, completo: boolean): string | undefined {
  const d = somenteDigitos(mascararTelefone(valor));
  if (d.length === 0 || !completo) return undefined;
  return d.length === 10 || d.length === 11 ? undefined : 'Telefone incompleto — DDD e número.';
}

function nomeDoDocumento(tipo: TipoDeDocumento, digitos: string): string {
  if (tipo === 'cpf') return 'CPF';
  if (tipo === 'cnpj') return 'CNPJ';
  return digitos.length <= 11 ? 'CPF' : 'CNPJ';
}

const PESOS_CNPJ_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;
const PESOS_CNPJ_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] as const;

function todosIguais(digitos: string): boolean {
  return new Set(digitos).size === 1;
}

function digitoVerificador(digitos: string, n: number, pesoInicial: number): number {
  let soma = 0;
  for (let i = 0; i < n; i += 1) soma += Number(digitos[i]) * (pesoInicial - i);
  return resto(soma);
}

function digitoPorPesos(digitos: string, pesos: readonly number[]): number {
  let soma = 0;
  for (let i = 0; i < pesos.length; i += 1) soma += Number(digitos[i]) * (pesos[i] as number);
  return resto(soma);
}

function resto(soma: number): number {
  const r = soma % 11;
  return r < 2 ? 0 : 11 - r;
}
